import type React from "react";
import { cn } from "../../lib/utils";
import { useDelayedFade } from "./skeleton";
import { Icon, type IconProps } from "./icon";

export type SpinnerProps = Omit<IconProps, "name" | "as"> & {
  /**
   * Invisible for its first ~200ms, then fades in (the Skeleton contract).
   * For DECISION gates — a branch that may redirect or swap trees, where a
   * content-shaped skeleton would promise the wrong page — so the common
   * fast path (cached flags, booted providers) never shows anything.
   */
  delayed?: boolean;
};

/**
 * The loading glyph — the `loader` Icon spinning. Being an Icon, it takes the
 * semantic `size` tokens and `color` names, and always emits its own `size-*`
 * class. Defaults to `xl` (24px, the standalone/centered idiom); pass a
 * smaller token where it sits inline with text or controls.
 */
export function Spinner({
  className,
  size = "xl",
  delayed = false,
  ...props
}: SpinnerProps): React.ReactElement {
  const fade = useDelayedFade(delayed);
  return (
    <Icon
      aria-label="Loading"
      className={cn(
        "animate-spin",
        fade,
        className,
      )}
      name="loader"
      role="status"
      size={size}
      {...props}
    />
  );
}
